/**
 * useAdminFeatureAccess — lee qué features del panel están ocultas para el admin
 * regular (configurado por el super admin en /admin/access-control).
 *
 * Consume GET /api/admin/feature-access → { hidden: string[] }.
 * Fail-open: si el endpoint falla, devolvemos hiddenSet vacío (mostrar todo) en
 * vez de bloquear el panel entero. El super admin ve todo igual.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

const FEATURE_ACCESS_QUERY_KEY = ["admin", "feature-access"] as const;

export interface AdminFeatureAccess {
  /** Feature keys (paths) ocultas para el admin regular. */
  hiddenSet: Set<string>;
  /** true cuando ya tenemos una respuesta definitiva (success o error). */
  isLoaded: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export function useAdminFeatureAccess(): AdminFeatureAccess {
  const { adminFetch, isLoaded: authLoaded } = useAdminAuth();

  const { data, isSuccess, isError, isFetching, refetch } = useQuery({
    queryKey: FEATURE_ACCESS_QUERY_KEY,
    enabled: authLoaded,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<string[]> => {
      const res = await adminFetch("/api/admin/feature-access");
      const body: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          body && typeof body === "object" && "error" in body
            ? String((body as { error: unknown }).error)
            : `HTTP ${res.status}`;
        throw new Error(msg);
      }
      const hidden = (body as { hidden?: unknown })?.hidden;
      return Array.isArray(hidden)
        ? hidden.filter((x): x is string => typeof x === "string")
        : [];
    },
  });

  const hiddenSet = useMemo(() => new Set(data ?? []), [data]);

  return {
    hiddenSet,
    // Fail-open: tras un error tratamos como "cargado sin nada oculto".
    isLoaded: isSuccess || isError,
    isFetching,
    refetch: () => void refetch(),
  };
}

/** Invalida el cache tras guardar toggles en la página de control. */
export function useInvalidateAdminFeatureAccess() {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: FEATURE_ACCESS_QUERY_KEY }),
    [queryClient],
  );
}
